import { Component, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { VenueContextService } from '../services/venue-context.service';
import { AnalyticsService } from '../services/analytics.service';
import {
  ChatService,
  ChatMessage,
  ChatDecision,
  ChatDestination,
  isTicketProposal,
} from '../services/chat.service';

type TicketStatus = 'pending' | 'filed' | 'dismissed';

interface ChatDisplayMessage extends ChatMessage {
  ticketStatus?: TicketStatus;
}

/**
 * Floating support-assistant bubble, ported from the customer portal's
 * ChatButtonComponent. Mounted once in MainComponent so it is only rendered
 * after the super-admin login gate has passed.
 *
 * Unlike the portal it is visible on every page: super-admin routes are not
 * all venue-scoped. The routed venue (if any) is read from VenueContextService
 * so the assistant can deep-link into `/venues/:id/...` pages.
 */
@Component({
  selector: 'app-chat-button',
  imports: [FormsModule, RouterLink],
  templateUrl: './chat-button.component.html',
  styleUrl: './chat-button.component.css',
})
export class ChatButtonComponent {
  private venueContext = inject(VenueContextService);
  private chatService = inject(ChatService);
  private analytics = inject(AnalyticsService);

  readonly chatBody = viewChild<ElementRef<HTMLElement>>('chatBody');

  readonly isChatOpen = signal(false);
  readonly isSending = signal(false);
  readonly messageText = signal('');
  readonly messages = signal<ChatDisplayMessage[]>([]);
  readonly conversationId = signal<string | null>(null);

  // Shared app-wide venue context (one listener, owned by VenueContextService).
  readonly venueId = this.venueContext.venueId;
  readonly venueName = computed(() => this.venueContext.venue()?.name ?? null);

  toggleChat(): void {
    this.isChatOpen.update(open => !open);
    if (this.isChatOpen()) {
      this.track('chat_open');
      this.scrollToBottom();
    }
  }

  /** Clear the transcript and drop the server conversation id. */
  newConversation(): void {
    if (this.isSending()) return;
    this.messages.set([]);
    this.conversationId.set(null);
    this.messageText.set('');
    this.track('chat_reset');
  }

  async sendMessage(): Promise<void> {
    const text = this.messageText().trim();
    if (!text || this.isSending()) return;

    this.track('chat_message_send', { new_conversation: !this.conversationId() });

    this.messages.update(msgs => [...msgs, {
      id: '',
      role: 'user',
      content: text,
      timestamp: new Date(),
    }]);
    this.messageText.set('');
    this.scrollToBottom();

    this.isSending.set(true);
    try {
      const response = await this.chatService.sendMessage(
        text,
        this.conversationId() ?? undefined
      );
      this.conversationId.set(response.conversationId);
      this.appendDecision(response.decision);
    } catch (error) {
      this.appendError('send', error);
    } finally {
      this.isSending.set(false);
      this.scrollToBottom();
    }
  }

  async confirmTicket(index: number): Promise<void> {
    const msg = this.messages()[index];
    if (!msg?.ticket || msg.ticketStatus !== 'pending' || this.isSending()) return;

    this.track('chat_ticket_confirm', { category: msg.ticket.category });

    this.isSending.set(true);
    try {
      const response = await this.chatService.sendMessage(
        '',
        this.conversationId() ?? undefined,
        { type: 'create_ticket', payload: msg.ticket }
      );
      this.conversationId.set(response.conversationId);
      this.setTicketStatus(index, 'filed');
      this.appendDecision(response.decision);
    } catch (error) {
      this.appendError('confirm_ticket', error);
    } finally {
      this.isSending.set(false);
      this.scrollToBottom();
    }
  }

  dismissTicket(index: number): void {
    const msg = this.messages()[index];
    if (msg?.ticketStatus !== 'pending' || this.isSending()) return;
    this.track('chat_ticket_dismiss', { category: msg.ticket?.category ?? '' });
    this.setTicketStatus(index, 'dismissed');
  }

  formatCategory(category: string): string {
    return category.replace(/_/g, ' ');
  }

  /** Resolve the assistant's route template against the routed venue. */
  getDestinationPath(destination: ChatDestination): string {
    const venueId = this.venueId() || '';
    return destination.path.replace(':venueId', venueId).replace(':id', venueId);
  }

  /** Uses the raw template path (placeholder intact) so GA aggregates by
   *  destination instead of fragmenting per venue. */
  trackDestinationClick(destination: ChatDestination): void {
    this.track('chat_destination_click', { path: destination.path, label: destination.label });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private appendDecision(decision: ChatDecision): void {
    this.track('chat_reply', {
      mode: decision.mode,
      intent: decision.intent,
      confidence: decision.confidence,
    });
    this.messages.update(msgs => [...msgs, {
      id: '',
      role: 'assistant',
      content: decision.reply,
      timestamp: new Date(),
      mode: decision.mode,
      intent: decision.intent,
      confidence: decision.confidence,
      destination: decision.destination || null,
      ticket: decision.mode === 'confirm_ticket' && isTicketProposal(decision.ticket) ? decision.ticket : null,
      ticketStatus: decision.mode === 'confirm_ticket' && isTicketProposal(decision.ticket) ? 'pending' : undefined,
    }]);
  }

  private appendError(action: 'send' | 'confirm_ticket', error: unknown): void {
    console.error('[chat] request failed', action, error);
    this.track('chat_error', { action });
    this.messages.update(msgs => [...msgs, {
      id: '',
      role: 'assistant',
      content: 'Sorry, something went wrong. Please try again.',
      timestamp: new Date(),
    }]);
  }

  private setTicketStatus(index: number, status: TicketStatus): void {
    this.messages.update(msgs =>
      msgs.map((m, i) => (i === index ? { ...m, ticketStatus: status } : m))
    );
  }

  /** Log a chat event to GA. Message content is never sent, only metadata. */
  private track(event: string, params: { [key: string]: any } = {}): void {
    try {
      this.analytics.logEvent(event, {
        feature: 'chat',
        venue_id: this.venueId() ?? '',
        ...params,
      });
    } catch {
      // Analytics blocked (ad blocker, offline). Never let it break the chat.
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.chatBody()?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
